import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React, {
  ChangeEvent,
  forwardRef,
  RefObject,
  useEffect,
  useId,
  useState,
} from 'react';
import { useBackgroundColor } from '../../../utils';
import { Cancel, Expand, Filter } from '../../icon';
import { Stack } from '../../stack/Stack';
import { Typography } from '../../typography/Typography';
import { BaseFormProps, StyledBase } from '../base/Base';
import { FormField, FormFieldProps } from '../formField/FormField';
import { Grid } from '../../grid/Grid';
import tokens from './tokens';

export interface FilterInputProps extends BaseFormProps, FormFieldProps {
  options: Options[];
  onChange: (...event: any[]) => void;
  disableFilter?: boolean;
  externalFilter?: (value: string | number) => void;
  externalLoading?: boolean;
  allowExternalUpdates?: boolean;
  constrained?: boolean;
  isInactive?: boolean;
  centered?: boolean;
  showFilterIcon?: boolean;
}

export interface Options {
  name: string;
  value: string | number;
}

interface StyledOptionProps {
  selected?: boolean;
  centered?: boolean;
}

interface StyledFilterInputProps {
  showFilterIcon?: boolean;
}

// Styles

const StyledFilterInput = styled(Grid)<StyledFilterInputProps>`
  ${({ theme: { space }, showFilterIcon }) => css`
    > * {
      position: relative;
    }

    > div > button:first-of-type {
      ${showFilterIcon &&
      css`
        padding-left: 56px;
      `}
    }
  `}
`;

const rotateAni = keyframes`
  0% {
    transform: translatex(-50%) translatey(-50%) rotate(0deg);
  }
  100% {
    transform: translatex(-50%) translatey(-50%) rotate(360deg);
  }
  `;

const StyledButton = styled.button<{ isLoading: boolean }>`
  ${({ theme: { space }, isLoading }) => css`
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    padding: 0 ${space.small};

    ${isLoading &&
    css`
      transition: all 0.3s linear;
      color: transparent;
      &:before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translatex(-50%) translatey(-50%);
        display: inline-block;
        width: ${space.medium};
        height: ${space.medium};
        border: 2px solid;
        border-radius: 50%;
        border-top-color: rgba(0, 0, 0, 0.7);
        border-left-color: rgba(0, 0, 0, 0.1);
        border-right-color: rgba(0, 0, 0, 0.5);
        border-bottom-color: rgba(0, 0, 0, 0.3);
        animation: ${rotateAni} 0.6s linear infinite;
        display: block;
      }
    `}
  `}
`;

const StyledOptions = styled.div`
  ${({ theme: { space, colors, borderRadii, layers } }) => css`
    position: absolute;
    right: 0;
    top: calc(100% + ${space.xxSmall});
    width: 100%;
    background: ${colors.utilityWhite};
    border-radius: ${borderRadii[tokens.optionsBorderRadius]};
    border: solid 1px ${colors[tokens.optionsBorderColor]};
    box-shadow: 0 0 0 1px ${colors[tokens.optionsBorderColor]};
    padding: ${space.xSmall};
    z-index: ${layers.filterInput};

    /* Add text overflow handling */
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    padding-right: ${space.medium};
  `}
`;

const StyledOptionsInner = styled(Stack)`
  ${({ theme: { space, colors, borderRadii } }) => css`
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-color: ${colors[tokens.optionsBackgroundColor]}
      ${colors.utilityBackground};
    max-height: 330px;
    padding: ${space.xxSmall};

    &::-webkit-scrollbar-track {
      border-radius: ${borderRadii.medium};
      background-color: ${colors.utilityBackground};
    }
    &::-webkit-scrollbar {
      width: ${space.xxSmall};
      background-color: ${colors.utilityBackground};
    }
    &::-webkit-scrollbar-thumb {
      border-radius: ${borderRadii.medium};
      background-color: ${colors[tokens.optionsBackgroundColor]};
    }
  `}
`;

const StyledOption = styled(Typography)<StyledOptionProps>`
  ${({ theme: { space, colors, borderRadii }, selected, centered }) => css`
    ${useBackgroundColor(
      selected
        ? tokens.optionsBackgroundColor
        : tokens.optionsBackgroundColorUnselected
    )}
    display: block;
    padding: ${space.xxSmall} ${space.small};
    border-radius: ${borderRadii.medium};
    width: 100%;
    text-align: ${centered ? 'center' : 'left'};

    /* Add text overflow handling */
    white-space: normal;
    overflow-wrap: break-word;
    word-break: break-word;

    &:hover {
      ${useBackgroundColor(
        selected
          ? tokens.optionsBackgroundColor
          : tokens.optionsHoverBackgroundColor
      )}
      cursor: ${selected ? 'default' : 'pointer'};
      ${tokens.optionsHoverUnderline &&
      css`
        text-decoration: underline;
      `}
    }
  `}
`;

const StyledFilterIcon = styled(Filter)`
  ${({ theme: { space } }) => css`
    position: absolute;
    left: ${space.small};
    top: 50%;
    transform: translateY(-50%);
  `}
`;

// JSX

export const FilterInput = forwardRef<HTMLSelectElement, FilterInputProps>(
  (
    {
      label,
      id,
      invalid,
      valid,
      defaultValue,
      placeholder,
      required,
      errorMessage,
      hideLabel,
      disabled,
      isInactive,
      hintText,
      optional,
      extraInformation,
      options,
      disableFilter,
      externalFilter,
      allowExternalUpdates,
      large,
      centered,
      externalLoading,
      onChange,
      constrained,
      labelNote,
      onFocus,
      showFilterIcon,
      ...rest
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string | number | undefined>();
    const [showOptions, setShowOptions] = useState<boolean>(false);
    const [filteredOptions, setFilterdptions] = useState<Options[]>(options);
    const [activeSuggestionIndex, setActiveSuggestionIndex] =
      useState<number>(-1);

    useEffect(() => {
      if (defaultValue) {
        setInputValue(defaultValue);
      }
    }, [defaultValue]);

    const handleOptionClick = (
      newValue: string | number,
      name: string | number,
      index: number
    ) => {
      setInputValue(name);
      setShowOptions(false);
      setFilterdptions(options);
      setActiveSuggestionIndex(index);
      if (onChange) {
        onChange(newValue);
      }
    };
    // if isInactive then remove the selcted value

    useEffect(() => {
      if (isInactive) {
        setInputValue(undefined);
      }
    }, [isInactive]);

    // if external filter is used
    // we need to set  the filtered input value

    useEffect(() => {
      if (externalFilter || allowExternalUpdates) {
        setFilterdptions(options);
      }
    }, [externalFilter, inputValue, options, allowExternalUpdates]);

    // On change to input

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      // if external API call is needed
      if (externalFilter) {
        externalFilter(value);
        setShowOptions(true);
        setInputValue(value);
        return;
      }
      // or local filtering

      setInputValue(value);
      onChange('');
      setActiveSuggestionIndex(0);
      if (value.length >= 3) {
        const optionList = options.filter((option) =>
          option.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilterdptions(optionList);
        setShowOptions(true);
      } else {
        setShowOptions(false);
        setFilterdptions(options);
      }
    };

    // Keyboard support

    const onKeyDown = (e: KeyboardEvent) => {
      // User pressed the enter key
      if (e.key === 'Enter') {
        e.preventDefault();
        const selectedOption = filteredOptions[activeSuggestionIndex] ?? {};

        setInputValue(selectedOption.name);
        onChange(selectedOption.value);
        setActiveSuggestionIndex(0);
        setShowOptions(false);
      }

      // User pressed the up arrow
      else if (e.key === 'ArrowUp') {
        if (activeSuggestionIndex === 0) {
          return;
        }

        setActiveSuggestionIndex(activeSuggestionIndex - 1);
      }
      // User pressed the down arrow
      else if (e.key === 'ArrowDown') {
        if (activeSuggestionIndex - 1 === filteredOptions?.length) {
          return;
        }

        setActiveSuggestionIndex(activeSuggestionIndex + 1);
      }
    };

    const filterInputOptionsContainerId = useId();

    return (
      <FormField
        fieldAs="fieldset"
        label={label}
        htmlFor={id}
        invalid={invalid}
        errorMessage={errorMessage}
        hideLabel={hideLabel}
        hintText={hintText}
        disabled={disabled}
        optional={optional}
        extraInformation={extraInformation}
        {...rest}
      >
        <Stack gap="xxSmall">
          {labelNote && <span>{labelNote}</span>}
          <StyledFilterInput
            desktopColumns={constrained ? '2' : 'none'}
            showFilterIcon={showFilterIcon}
          >
            <div>
              {disableFilter ? (
                <StyledBase
                  onClick={() => setShowOptions(!showOptions)}
                  placeholder={placeholder}
                  valid={valid}
                  invalid={invalid}
                  id={id}
                  ref={ref as unknown as RefObject<HTMLInputElement>}
                  as="button"
                  type="button"
                  leftAligned
                  centerAligned={centered}
                  large={large}
                  onFocus={onFocus}
                  aria-expanded={showOptions}
                  aria-controls={filterInputOptionsContainerId}
                >
                  {inputValue || placeholder}
                </StyledBase>
              ) : (
                <StyledBase
                  onChange={handleOnChange}
                  value={inputValue}
                  placeholder={placeholder}
                  onKeyDown={onKeyDown}
                  valid={valid}
                  invalid={invalid}
                  id={id}
                  ref={ref as unknown as RefObject<HTMLInputElement>}
                  active={showOptions}
                  autoComplete="off"
                  large={large}
                  onFocus={onFocus}
                  role="combobox"
                  aria-expanded={showOptions}
                  aria-controls={filterInputOptionsContainerId}
                />
              )}

              <StyledButton
                type="button"
                onClick={() => setShowOptions(!showOptions)}
                id={`${id}-action`}
                isLoading={externalLoading ?? false}
                aria-label={showOptions ? 'Close' : 'Expand'}
              >
                {showOptions ? (
                  <Cancel title="Cancel" />
                ) : (
                  <Expand title="Expand" />
                )}
              </StyledButton>
              {showOptions && (
                <StyledOptions id={filterInputOptionsContainerId}>
                  <StyledOptionsInner
                    gap="xxSmall"
                    id={`${id}-options`}
                    role="group"
                  >
                    {filteredOptions.length >= 1 ? (
                      <>
                        {/* Options */}
                        {filteredOptions.map((option, index) => (
                          <StyledOption
                            key={`${id}-${index}`}
                            type="button"
                            data-test={option.value}
                            onClick={() => {
                              handleOptionClick(
                                option.value,
                                option.name,
                                index
                              );
                            }}
                            id={`${id}-${index}`}
                            component="button"
                            variant="bodyMedium"
                            selected={index === activeSuggestionIndex}
                            aria-selected={index === activeSuggestionIndex}
                            role="option"
                          >
                            {option.name}
                          </StyledOption>
                        ))}
                      </>
                    ) : (
                      <StyledOption>
                        {externalLoading ? 'Loading' : 'No results found'}
                      </StyledOption>
                    )}
                  </StyledOptionsInner>
                </StyledOptions>
              )}
              {showFilterIcon && <StyledFilterIcon color="brandOne" />}
            </div>
          </StyledFilterInput>
        </Stack>
      </FormField>
    );
  }
);

FilterInput.displayName = 'FilterInput';
